import type { components } from '@heidi/schema/ml-scribe';

import { getDebugLetterPayload } from '../debug/letter-sender-note-panel-debugger';
import { http } from './lettersHttp';

type LetterResponse = components['schemas']['LetterResponse'];
type ListLettersResponse = components['schemas']['LetterListResponse'];
type LetterHeadResponse = components['schemas']['LetterheadPreviewResponse'];
type LetterAttachmentListResponse = components['schemas']['LetterAttachmentListResponse'];
type LetterTimelineEventListResponse =
  components['schemas']['LetterTimelineEventListResponse'];
type LetterAttachmentDownloadUrlResponse =
  components['schemas']['LetterAttachmentDownloadUrlResponse'];
type LetterAttachmentResponse = components['schemas']['LetterAttachmentResponse'];
type SendLetterResponse = components['schemas']['SendLetterResponse'];
type SignLetterRequest = components['schemas']['SignLetterRequest'];
type UpdateLetterRequest = components['schemas']['UpdateLetterRequest'];
type CreateLetterFromDocumentRequest =
  components['schemas']['CreateLetterFromDocumentRequest'];
type RecipientSearchResponse = components['schemas']['RecipientSearchResponse'];

export const createLetterFromDocument = (
  sessionId: string,
  body: CreateLetterFromDocumentRequest
) => {
  return http().post<LetterResponse>(`/sessions/${sessionId}/letters`, body);
};

export const createLetterFromCurrentNote = (sessionId: string) => {
  return http().post<LetterResponse>(`/sessions/${sessionId}/letters:from-consult-note`);
};

export const getLetters = (sessionId: string) => {
  const debugLetterPayload = getDebugLetterPayload(sessionId);

  if (debugLetterPayload) {
    return Promise.resolve(debugLetterPayload);
  }

  return http().get<ListLettersResponse>(`/sessions/${sessionId}/letters`);
};

export const deleteLetter = (sessionId: string, letterId: string) => {
  return http().delete(`/sessions/${sessionId}/letters/${letterId}`);
};

export const getLetterHead = (sessionId: string, letterId: string) => {
  return http().get<LetterHeadResponse>(
    `/sessions/${sessionId}/letters/${letterId}/letterhead`
  );
};

export const getLetterAttachmentsV2 = (sessionId: string, letterId: string) => {
  return http().get<LetterAttachmentListResponse>(
    `/sessions/${sessionId}/letters/${letterId}/attachments`
  );
};

export const getLetterTimelineEvents = (sessionId: string, letterId: string) => {
  return http().get<LetterTimelineEventListResponse>(
    `/sessions/${sessionId}/letters/${letterId}/timeline-events`
  );
};

export const getLetterAttachmentDownloadUrlV2 = (
  sessionId: string,
  letterId: string,
  attachmentId: string
) => {
  return http().get<LetterAttachmentDownloadUrlResponse>(
    `/sessions/${sessionId}/letters/${letterId}/attachments/${attachmentId}/download-url`
  );
};

export const deleteLetterAttachmentV2 = (
  sessionId: string,
  letterId: string,
  attachmentId: string
) => {
  return http().delete(
    `/sessions/${sessionId}/letters/${letterId}/attachments/${attachmentId}`
  );
};

export const uploadLetterAttachmentV2 = ({
  sessionId,
  letterId,
  file,
  onProgressUpdate,
  signal,
}: {
  sessionId: string;
  letterId: string;
  file: File;
  onProgressUpdate?: (progress: number) => void;
  signal?: AbortSignal;
}) => {
  const formData = new FormData();
  formData.append('file', file);

  return http().post<LetterAttachmentResponse>(
    `/sessions/${sessionId}/letters/${letterId}/attachments`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      signal,
      onUploadProgress: (progressEvent) => {
        if (progressEvent.total && onProgressUpdate) {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          onProgressUpdate(percentCompleted);
        }
      },
    }
  );
};

export const sendLetter = ({
  sessionId,
  letterId,
  pdfFile,
}: {
  sessionId: string;
  letterId: string;
  pdfFile: File;
}) => {
  const formData = new FormData();
  formData.append('file', pdfFile);

  return http().put<SendLetterResponse>(
    `/sessions/${sessionId}/letters/${letterId}:send`,
    formData
  );
};

export const signLetter = (
  sessionId: string,
  letterId: string,
  body: SignLetterRequest
) => {
  return http().put<LetterResponse>(
    `/sessions/${sessionId}/letters/${letterId}:sign`,
    body
  );
};

export const updateLetter = (
  sessionId: string,
  letterId: string,
  body: UpdateLetterRequest
) => {
  return http().patch<LetterResponse>(`/sessions/${sessionId}/letters/${letterId}`, body);
};

export const searchLetterRecipients = (sessionId: string, params: { name: string }) => {
  return http().get<RecipientSearchResponse>(
    `/sessions/${sessionId}/letters:search-recipients`,
    {
      params,
    }
  );
};
